import {Component, OnInit, OnDestroy, Input, ViewChild} from '@angular/core';
import {AdminService} from '../../../_services/admin.service';
import {Permissions} from '../../../_services/permissions';
import * as FileSaver from 'file-saver';
import { ToastsManager } from 'ng2-toastr';
import {ACLFeature} from '../../../_models/acl-feature';
import {Log} from 'ng2-logger';
import {IActionMapping, ITreeOptions, TreeComponent} from 'angular-tree-component';
import {forEach} from '@angular/router/src/utils/collection';
import {childOfKind} from 'tslint';

const log = Log.create('FamilyLabelsComponent');

@Component({
  selector: 'edit-family-labels',
  styleUrls: ['familylabels.less'],
  templateUrl: 'familylabels.component.html',
})
export class FamilyLabelsComponent implements OnInit, OnDestroy {

  @Input() regions;
  canManageFamilyLabels = false;
  canExportFamilyLabels = false;
  canImportFamilyLabels = false;
  uploadError = '';
  type: string;
  id: number;
  parentId: any;
  isExporting = false;
  showUploadSuccessMessage = false;
  selectedRegion = null;

  moduleFamiliesLoaded = true;
  driverFamiliesLoaded = true;

  moduleFamilyNodes = [];
  driverFamilyNodes = [];

  selectedModuleFamilyIds = [];
  selectedDriverFamilyIds = [];

  actionMapping: IActionMapping = {
    mouse: {
      click: (tree, node) => {
        this.check(node, !node.data.checked);
        this.updateSelectedFamilies();
      }
    }
  };

  treeOptions: ITreeOptions = {
    actionMapping: this.actionMapping,
  };

  @ViewChild('modulestree')
  private modulestree: TreeComponent;

  @ViewChild('driverstree')
  private driverstree: TreeComponent;

  constructor(
    private admin: AdminService,
    private permissions: Permissions,
    private toast: ToastsManager) {}

  ngOnInit() {
    log.d('ngOnInit');

    this.permissions.hasFeature(ACLFeature.EXPORT_LABELING_SHEET)
      .subscribe((success) => {
        this.canManageFamilyLabels = success;
        this.canExportFamilyLabels = success;
      });

    this.permissions.hasFeature(ACLFeature.IMPORT_LABELING_SHEET)
      .subscribe((success) => {
        this.canManageFamilyLabels = success;
        this.canImportFamilyLabels = success;
      });
  }
  private onFamilyLabelExport(region = null) {
    this.isExporting = true;
    this.admin.familyLabelExport(this.selectedModuleFamilyIds, this.selectedDriverFamilyIds).subscribe((response) => {
      FileSaver.saveAs(response, `export-family-labels-${Date.now()}.xlsx`);
      this.isExporting = false;
    });
  }

  onFamilyLabelImport($event) {
    this.uploadError = '';
    this.showUploadSuccessMessage = false;
    const fileElement = $event.srcElement;
    if (fileElement.files && fileElement.files[0]) {
      const file = fileElement.files[0];
      const fileExtension = file.name.split('.').pop();
      if (fileExtension === 'xlsx') {
        this.showUploadSuccessMessage = true;
        this.admin.familyLabelImport(file).subscribe(() => {
          this.showUploadSuccessMessage = true;
          this.toast.success(`Family Label import started successfully. ` +
            `The import will be done in a short while, depending of the amount of combinations uploaded.`,
            null,
            {
              toastLife: 10000
            });
        }, (error) => {
          this.toast.error(`Excel imported failed`);
          this.uploadError = JSON.parse(error._body).message;
        });
      } else {
        this.uploadError = 'Please provide a valid excel file!';
      }
    } else {
      this.uploadError = 'Please provide a file!';
    }
  }

  onSelectRegion(region) {
    if (this.selectedRegion === region) { return; }

    this.selectedRegion = region;
    this.fetchData();
  }

  public toggleAllChecks(type) {
    let tree, model, newCheckState;

    if (type === 'modules') {
      tree = 'modulestree';
      model = this.selectedModuleFamilyIds;
    } else if (type === 'drivers') {
      tree = 'driverstree';
      model = this.selectedDriverFamilyIds;
    }

    newCheckState = !model.length; // decide to either select everything or nothing

    for (const node of this[tree].treeModel.nodes) {
      this.check(this[tree].treeModel.getNodeById(node.id), newCheckState);
    }
    this.updateSelectedFamilies();
  }

  private fetchData() {
    this.moduleFamiliesLoaded = false;
    this.moduleFamiliesLoaded = false;

    // TODO: should be done with another API call that has no products. For now, I remove the products manually.
    this.admin.getModuleTree(this.selectedRegion).subscribe((response) => {
      const nodes = response.data;
      for (const node of nodes) {
        this.removeProductsRecursively(node);
      }

      this.moduleFamilyNodes = nodes;
      this.moduleFamiliesLoaded = true;
    });

    this.admin.getDriversTree(this.selectedRegion).subscribe((response) => {
      const nodes = response.data;
      for (const node of nodes) {
        this.removeProductsRecursively(node);
      }

      this.driverFamilyNodes = nodes;
      this.driverFamiliesLoaded = true;
    });
  }

  // TODO: should already have been done by an API call
  public removeProductsRecursively(node) {
    if (node.type === 'product') { return; }

    if (node.children && node.children.length ) {
      node.children = node.children.filter(child => {
        return child.type !== 'product';
      });

      for (const child of node.children) {
        this.removeProductsRecursively(child);
      }
    }
  }

  public check(node, checked) {
    this.updateChildNodeCheckbox(node, checked);
    this.updateParentNodeCheckbox(node.realParent);
  }

  public updateChildNodeCheckbox(node, checked) {
    node.data.checked = checked;
    if (node.children) {
      node.children.forEach((child) => this.updateChildNodeCheckbox(child, checked));
    }
  }

  public updateParentNodeCheckbox(node) {
    if (!node) {
      return;
    }

    let allChildrenChecked = true;
    let noChildChecked = true;

    for (const child of node.children) {
      if (!child.data.checked || child.data.indeterminate) {
        allChildrenChecked = false;
      }
      if (child.data.checked) {
        noChildChecked = false;
      }
    }

    if (allChildrenChecked) {
      node.data.checked = true;
      node.data.indeterminate = false;
    } else if (noChildChecked) {
      node.data.checked = false;
      node.data.indeterminate = false;
    } else {
      node.data.checked = true;
      node.data.indeterminate = true;
    }
    this.updateParentNodeCheckbox(node.parent);
  }

  updateSelectedFamilies() {
    this.selectedModuleFamilyIds = [];
    this.selectedDriverFamilyIds = [];

    if (this.modulestree) {
      this.modulestree.treeModel.nodes.forEach(node => this.findSelectedFamilyIdsRecursive(node, this.selectedModuleFamilyIds));
    }
    if (this.driverstree) {
      this.driverstree.treeModel.nodes.forEach(node => this.findSelectedFamilyIdsRecursive(node, this.selectedDriverFamilyIds));
    }
  }

  findSelectedFamilyIdsRecursive(node, collection) {
    if (node.checked) {
      collection.push(node.id);
    }

    if (node.children && node.children.length ) {
      for (const child of node.children) {
        this.findSelectedFamilyIdsRecursive(child, collection);
      }
    }
  }

  ngOnDestroy() {
  }
}



// WEBPACK FOOTER //
// ./src/app/management/components/familylabels/familylabels.component.ts